import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Minus, Trash2, ChefHat, ArrowLeft, CreditCard, Banknote, Wallet, Search, X, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { tableLabel } from '../lib/tableLabel';

/**
 * Reusable order-editor sheet.
 *
 * Props:
 *   open: bool
 *   onOpenChange: fn
 *   order: the current order document (must include `items` + `id`)
 *   actorRole: 'customer' | 'manager'   — controls payment-locked items + headline copy
 *   onSaved: fn(updatedOrder) called after a successful PATCH
 *
 * Behaviour:
 *   - Quantity steppers / remove for every existing line.
 *   - "Add item" picker that lists every available menu item with their default
 *     price; new line lets you set qty + optional spice level + chef note.
 *   - Submits a single PATCH /api/orders/{id} with the full new items array.
 *   - For PAID orders, a price delta triggers a confirmation step:
 *       diff > 0           -> "Collect $X at counter" (cash/EFTPOS)
 *       diff < 0  + card   -> "Refund $X to card" (auto-executes Square refund)
 *       diff < 0  + cash   -> "Refund $X in cash"
 *     Backend returns 409 on first save; we surface this UI and call PATCH
 *     again with confirm_payment_action set once the manager confirms.
 */
export default function OrderEditDialog({ open, onOpenChange, order, actorRole = 'manager', onSaved }) {
  const [items, setItems] = useState([]);
  const [menu, setMenu] = useState([]);
  const [picker, setPicker] = useState('');
  // 2026-02: Owner reported the picker was a long unsearchable list. Added a
  // search box (matches name + category + subcategory + tags) and grouped
  // the filtered results into category sections so finding "burger" /
  // "shisha apple" / "mojito" is one keystroke away.
  const [pickerSearch, setPickerSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // Settlement step state — when set, the items editor hides and we show the
  // "collect / refund" confirmation panel instead.
  const [pendingAction, setPendingAction] = useState(null);
  // Manager-override prompt — opens when the backend rejects the edit
  // because some line is past `received`. Manager / director can re-submit
  // with their PIN + a written reason; everyone else gets a hard stop
  // via toast. Tracked separately from pendingAction because the two
  // flows are mutually exclusive and we don't want to mix state shapes.
  const [pendingOverride, setPendingOverride] = useState(false);
  // ^ shape: { required_action, amount, direction, payment_method, old_total, new_total }
  // Manager-only "move table" state. We snapshot the original table_id on
  // open and let the manager pick a different one from the live tables list.
  const [tables, setTables] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const canMoveTable = actorRole === 'manager';

  useEffect(() => {
    if (!open) return;
    setPendingAction(null);
    setPendingOverride(false);
    setPickerSearch('');
    setPicker('');
    setSelectedTableId(order?.table_id || '');
    setItems((order?.items || []).map(it => ({
      item_id: it.item_id,
      quantity: it.quantity,
      variant_label: it.variant_label || null,
      head_label: it.head_label || null,
      spice_level: it.spice_level || '',
      instructions: it.instructions || '',
      _name: it.name,
      _price: it.price,
      _category: it.category,
      _status: it.status,
      _is_new: false,
    })));
    api.get('/menu').then(r => setMenu((r.data || []).filter(m => m.is_available !== false))).catch(() => {});
    if (canMoveTable) {
      api.get('/tables').then(r => setTables(r.data || [])).catch(() => {});
    }
  }, [open, order, canMoveTable]);

  const subtotal = useMemo(() => items.reduce((s, it) => s + (it._price || 0) * (it.quantity || 0), 0), [items]);
  const discount = (order?.discount_label && order?.discount_amount > 0) ? Math.round(subtotal * 0.10 * 100) / 100 : 0;
  const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);

  const stepQty = (idx, delta) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(0, (it.quantity || 0) + delta) } : it));
  };
  const removeLine = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };
  const updateField = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const addPicked = () => {
    if (!picker) return;
    const m = menu.find(x => x.id === picker);
    if (!m) return;
    setItems(prev => [
      ...prev,
      {
        item_id: m.id, quantity: 1, variant_label: null, head_label: null,
        spice_level: '', instructions: '',
        _name: m.name, _price: m.price, _category: m.category,
        _status: 'received', _is_new: true,
      },
    ]);
    setPicker('');
  };

  const buildPayload = (confirmAction = null) => {
    const payload = {
      items: items
        .filter(it => (it.quantity || 0) > 0)
        .map(it => ({
          item_id: it.item_id,
          quantity: Number(it.quantity),
          variant_label: it.variant_label || null,
          head_label: it.head_label || null,
          spice_level: it.spice_level || null,
          instructions: it.instructions || null,
        })),
      confirm_payment_action: confirmAction,
    };
    // Only send table_id when the manager actually changed it — keeps the
    // payload small and avoids accidentally re-occupying the same table.
    if (canMoveTable && selectedTableId && selectedTableId !== order?.table_id) {
      payload.table_id = selectedTableId;
    }
    return payload;
  };

  const finalizeSuccess = (data) => {
    const act = data?._edit_action;
    if (act?.action === 'refund_card') {
      toast.success(`Refunded $${Number(act.amount).toFixed(2)} to customer card`);
    } else if (act?.action === 'refund_cash') {
      toast.success(`Cash refund of $${Number(act.amount).toFixed(2)} marked — hand to customer`);
    } else if (act?.action === 'collect') {
      toast.success(`Collect $${Number(act.amount).toFixed(2)} at bar — recorded`);
    } else if (data?.table_id !== order?.table_id) {
      toast.success(`Order moved to ${tableLabel(data.table_number)}`);
    } else {
      toast.success('Order updated');
    }
    onSaved && onSaved(data);
    onOpenChange(false);
  };

  const save = async () => {
    const payload = buildPayload(null);
    if (payload.items.length === 0) {
      toast.error('Order must have at least one item — use Void to cancel.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}`, payload);
      finalizeSuccess(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend asked us to confirm a cashier action before persisting.
      if (err.response?.status === 409 && detail?.code === 'payment_action_required') {
        setPendingAction(detail);
      } else if (
        // Locked-item gate from the Feb 2026 lock-rule overhaul. Manager+
        // can override with a PIN + reason; everyone else gets the toast
        // as a hard stop.
        typeof detail === 'string'
        && (detail.includes('being prepared') || detail.toLowerCase().includes('locked item') || detail.toLowerCase().includes('manager pin') || detail.toLowerCase().includes('manager override'))
        && ['manager', 'director'].includes(actorRole)
      ) {
        setPendingOverride(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Edit failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Manager-override retry: re-submits the SAME payload but with the
  // PIN + reason the manager just typed. Keeps a single source of truth
  // for what gets sent (no risk of the buildPayload result drifting
  // between the original attempt and the override retry).
  const submitOverride = async ({ pin, reason }) => {
    setSaving(true);
    try {
      const payload = { ...buildPayload(null), override_pin: pin, override_reason: reason };
      const { data } = await api.patch(`/orders/${order.id}`, payload);
      setPendingOverride(false);
      finalizeSuccess(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Override failed'));
    } finally {
      setSaving(false);
    }
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/orders/${order.id}`,
        buildPayload(pendingAction.required_action),
      );
      finalizeSuccess(data);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Edit failed');
    } finally {
      setSaving(false);
    }
  };

  // ------ render: manager-override prompt for locked items -------------
  if (pendingOverride) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setPendingOverride(false); onOpenChange(false); } }}>
        <DialogContent className="bg-bg-secondary border border-amber-500/40 max-w-md" data-testid="edit-override-step">
          <ManagerOverrideForm
            saving={saving}
            onCancel={() => setPendingOverride(false)}
            onSubmit={submitOverride}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // ------ render: cashier-action confirmation step ----------------------
  if (pendingAction) {
    const amt = Number(pendingAction.amount || 0).toFixed(2);
    const isCollect = pendingAction.direction === 'collect';
    const isCardRefund = pendingAction.required_action === 'refund_card';
    const isCashRefund = pendingAction.required_action === 'refund_cash';
    const Icon = isCollect ? Banknote : isCardRefund ? CreditCard : Wallet;
    const accent = isCollect ? 'text-emerald-300' : 'text-amber-300';
    const accentBg = isCollect ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30';
    const ctaBg = isCollect ? 'bg-emerald-500 hover:bg-emerald-400 text-black' : 'bg-amber-500 hover:bg-amber-400 text-black';
    const headline = isCollect
      ? 'Collect difference at bar'
      : isCardRefund
        ? 'Refund difference to card'
        : 'Refund difference in cash';
    const subcopy = isCollect
      ? 'Take this amount from the customer via EFTPOS or cash before confirming the edit.'
      : isCardRefund
        ? 'Confirming will charge a partial refund to the customer\u2019s original Square card payment.'
        : 'Hand this amount back to the customer in cash, then confirm the edit.';
    const ctaLabel = isCollect
      ? `Collected $${amt}`
      : isCardRefund
        ? `Refund $${amt} to card`
        : `Refunded $${amt} in cash`;

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-md" data-testid="edit-settlement-step">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <Icon className={`w-4 h-4 ${accent}`} />
              {headline}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className={`rounded-sm border ${accentBg} p-4 text-center`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-sans">{isCollect ? 'Amount to collect' : 'Amount to refund'}</p>
              <p className={`font-mono text-4xl font-bold mt-1 ${accent}`} data-testid="settlement-amount">${amt}</p>
              <p className="text-xs text-zinc-500 font-sans mt-2">
                ${Number(pendingAction.old_total || 0).toFixed(2)} <span className="px-1">&rarr;</span> ${Number(pendingAction.new_total || 0).toFixed(2)}
              </p>
            </div>
            <p className="text-sm text-zinc-400 font-sans leading-relaxed">{subcopy}</p>
            <div className="flex gap-2">
              <Button
                onClick={() => setPendingAction(null)}
                variant="ghost"
                className="flex-1 h-11 text-zinc-300 hover:bg-white/5 font-sans text-sm"
                data-testid="settlement-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to edit
              </Button>
              <Button
                onClick={confirmAction}
                disabled={saving}
                className={`flex-1 h-11 font-bold tracking-[0.08em] uppercase text-xs rounded-sm font-sans disabled:opacity-50 ${ctaBg}`}
                data-testid="settlement-confirm"
              >
                {saving ? 'Working\u2026' : ctaLabel}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ------ render: items editor (default) --------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-bg-secondary border border-white/10 max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-gold" />
            {actorRole === 'manager' ? 'Edit order' : 'Edit your order'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          {/* Manager-only: move this order to a different table */}
          {canMoveTable && tables.length > 0 && (
            <div className="bg-white/[0.03] border border-white/10 rounded-sm p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Table</Label>
                {selectedTableId && selectedTableId !== order?.table_id && (
                  <button
                    type="button"
                    onClick={() => setSelectedTableId(order?.table_id || '')}
                    className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-white font-sans"
                    data-testid="edit-table-reset"
                  >Reset</button>
                )}
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5 max-h-32 overflow-y-auto" data-testid="edit-table-grid">
                {tables.map(t => {
                  const isCurrent = t.id === order?.table_id;
                  const isSelected = t.id === selectedTableId;
                  const occupied = t.status === 'occupied' && !isCurrent;
                  const cls = isSelected
                    ? 'border-gold bg-gold/15 text-gold ring-1 ring-gold/40'
                    : occupied
                      ? 'border-red-500/30 text-red-300 hover:border-red-400'
                      : 'border-emerald-500/25 text-emerald-300 hover:border-emerald-400';
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedTableId(t.id)}
                      className={`px-2 py-2 rounded-sm border bg-white/[0.02] font-sans text-xs font-semibold transition-all ${cls}`}
                      data-testid={`edit-table-${t.number}`}
                      title={isCurrent ? 'Current table' : `${t.status} · ${t.capacity || ''} seats`}
                    >
                      {tableLabel(t.number)}
                      {isCurrent && <span className="block text-[9px] text-zinc-500 mt-0.5">current</span>}
                    </button>
                  );
                })}
              </div>
              {selectedTableId && selectedTableId !== order?.table_id && (
                <p className="text-[11px] text-amber-300 font-sans">
                  Moving from {tableLabel(order?.table_number)} → {tableLabel(tables.find(t => t.id === selectedTableId)?.number)} on save.
                </p>
              )}
            </div>
          )}

          {/* Lines */}
          {items.length === 0 && (
            <p className="text-sm text-zinc-500 font-sans py-4 text-center">No items — add at least one below.</p>
          )}
          {items.map((it, idx) => (
            <div key={`${it.item_id}-${idx}`} className="bg-white/[0.03] border border-white/5 rounded-sm p-3 space-y-2" data-testid={`edit-line-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-sans text-sm truncate">{it._name}</p>
                  <p className="text-zinc-500 font-sans text-xs">${(it._price || 0).toFixed(2)} each · {it._category}{it._is_new ? ' · NEW' : ''}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => stepQty(idx, -1)} className="w-7 h-7 rounded-sm bg-white/5 hover:bg-white/10 text-white flex items-center justify-center" data-testid={`qty-minus-${idx}`}><Minus className="w-3.5 h-3.5" /></button>
                  <span className="text-white font-mono text-sm w-6 text-center" data-testid={`qty-value-${idx}`}>{it.quantity}</span>
                  <button onClick={() => stepQty(idx, +1)} className="w-7 h-7 rounded-sm bg-white/5 hover:bg-white/10 text-white flex items-center justify-center" data-testid={`qty-plus-${idx}`}><Plus className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeLine(idx)} className="w-7 h-7 rounded-sm hover:bg-red-500/15 text-red-400 flex items-center justify-center ml-1" data-testid={`remove-line-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {it._category === 'food' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">Spice</Label>
                    <select value={it.spice_level || ''} onChange={e => updateField(idx, 'spice_level', e.target.value)} className="w-full mt-1 bg-transparent border border-white/10 text-white h-8 px-2 rounded-sm font-sans text-xs" data-testid={`spice-${idx}`}>
                      <option value="" className="bg-bg-primary">—</option>
                      <option value="mild" className="bg-bg-primary">Mild</option>
                      <option value="medium" className="bg-bg-primary">Medium</option>
                      <option value="hot" className="bg-bg-primary">Hot</option>
                      <option value="extra hot" className="bg-bg-primary">Extra hot</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">Note</Label>
                    <Input value={it.instructions || ''} onChange={e => updateField(idx, 'instructions', e.target.value)} className="mt-1 bg-transparent border border-white/10 text-white h-8 px-2 rounded-sm font-sans text-xs" placeholder="No onion, allergy…" data-testid={`note-${idx}`} />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add item picker — searchable + grouped by category.
              Owner reported the previous bare <select> was unusable for
              200+ items. Now: type to filter (name / category / subcategory
              / tags), tap a row to add it to the cart. */}
          <div className="bg-gold/5 border border-gold/15 rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-gold font-sans">Add item</Label>
              {pickerSearch && (
                <button
                  type="button"
                  onClick={() => setPickerSearch('')}
                  className="text-[10px] text-zinc-500 hover:text-zinc-300 font-sans flex items-center gap-1"
                  data-testid="add-item-search-clear"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
              <Input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search menu by name, category, tag…"
                className="pl-8 bg-bg-primary/40 border-white/10 text-white text-sm h-9 font-sans"
                autoFocus={false}
                data-testid="add-item-search-input"
              />
            </div>
            {(() => {
              const q = pickerSearch.trim().toLowerCase();
              const filtered = q
                ? menu.filter((m) => {
                    const hay = [m.name, m.category, m.subcategory, ...(m.tags || [])]
                      .filter(Boolean).join(' ').toLowerCase();
                    return hay.includes(q);
                  })
                : menu;
              // Group by category for scannability (matches the customer menu layout)
              const byCat = filtered.reduce((acc, m) => {
                const c = (m.category || 'other').toLowerCase();
                (acc[c] = acc[c] || []).push(m);
                return acc;
              }, {});
              const order = ['drinks', 'food', 'shisha', 'activities', 'manual_charge'];
              const cats = [
                ...order.filter((c) => byCat[c]),
                ...Object.keys(byCat).filter((c) => !order.includes(c)).sort(),
              ];
              if (filtered.length === 0) {
                return <p className="text-xs text-zinc-500 font-sans py-3 text-center">No items match "{pickerSearch}".</p>;
              }
              return (
                <div className="max-h-64 overflow-y-auto -mx-1 px-1 space-y-2" data-testid="add-item-results">
                  {cats.map((cat) => (
                    <div key={cat}>
                      <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 font-sans px-1 mb-1">
                        {cat.replace('_', ' ')} · {byCat[cat].length}
                      </p>
                      <div className="space-y-1">
                        {byCat[cat].slice(0, q ? 50 : 12).map((m) => (
                          <button
                            type="button"
                            key={m.id}
                            onClick={() => {
                              // Inline-add: skip the extra "confirm" click. Owner specifically
                              // asked for faster lookup, so each tap drops the item straight
                              // into the cart (qty 1) and keeps the search open for the next pick.
                              setPicker(m.id);
                              setItems(prev => [
                                ...prev,
                                {
                                  item_id: m.id, quantity: 1, variant_label: null, head_label: null,
                                  spice_level: '', instructions: '',
                                  _name: m.name, _price: m.price, _category: m.category,
                                  _status: 'received', _is_new: true,
                                },
                              ]);
                              toast.success(`Added ${m.name}`);
                            }}
                            className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-sm bg-white/[0.03] border border-white/5 hover:bg-gold/15 hover:border-gold/30 transition-colors text-left"
                            data-testid={`add-item-row-${m.id}`}
                          >
                            <div className="min-w-0">
                              <p className="text-white text-xs font-sans font-medium truncate">{m.name}</p>
                              {m.subcategory && (
                                <p className="text-[10px] text-zinc-500 font-sans truncate">{m.subcategory}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-gold font-mono text-xs">${Number(m.price || 0).toFixed(2)}</span>
                              <Plus className="w-3.5 h-3.5 text-gold/70" />
                            </div>
                          </button>
                        ))}
                      </div>
                      {!q && byCat[cat].length > 12 && (
                        <p className="text-[10px] text-zinc-600 italic font-sans px-1 mt-1">
                          + {byCat[cat].length - 12} more — type to search
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Totals */}
          <div className="bg-white/[0.03] border border-white/10 rounded-sm p-3 space-y-1">
            <div className="flex justify-between text-sm font-sans"><span className="text-zinc-400">Subtotal</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-sm font-sans"><span className="text-emerald-400">Member welcome 10%</span><span className="text-emerald-400">-${discount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between pt-1.5 border-t border-white/10 mt-1.5"><span className="text-white font-medium">New total</span><span className="text-gold font-bold" data-testid="edit-new-total">${total.toFixed(2)}</span></div>
          </div>

          <Button onClick={save} disabled={saving} className="w-full h-11 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans disabled:opacity-50" data-testid="edit-order-save">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   MANAGER OVERRIDE FORM
   Used when the order-edit / void path is blocked because a line
   item has moved past `received`. Manager + Director re-attest
   their authority by entering their kiosk PIN + a written reason.
   Backend cross-checks PIN against `users.kiosk_pin` and stamps
   the audit trail with both fields before any mutation.
   ============================================================ */
function ManagerOverrideForm({ saving, onCancel, onSubmit }) {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const canSubmit = !saving && /^\d{4,6}$/.test(pin) && reason.trim().length >= 3;
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-serif text-amber-300 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" /> Manager override required
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 pt-2 font-sans">
        <p className="text-zinc-300 text-sm leading-relaxed">
          This order has one or more items past <span className="text-amber-300 font-semibold">Received</span> and is locked.
          Manager or Director override is needed to continue. Your action will be audit-logged with your name, the original value, and the reason.
        </p>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">Your PIN (4–6 digits)</label>
          <input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
            className="w-full h-10 px-3 bg-white/[0.04] border border-white/10 rounded-sm text-white font-mono tracking-[0.4em] text-center text-lg focus:border-amber-500/60 focus:outline-none"
            data-testid="override-pin-input"
            autoFocus
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">Reason (required, min 3 chars)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 200))}
            rows={3}
            placeholder="e.g. customer changed mind, comped due to wait, allergy correction"
            className="w-full px-3 py-2 bg-white/[0.04] border border-white/10 rounded-sm text-white text-sm focus:border-amber-500/60 focus:outline-none resize-none"
            data-testid="override-reason-input"
          />
          <p className="text-[10px] text-zinc-600 mt-1">{reason.length}/200</p>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button
            onClick={onCancel}
            disabled={saving}
            className="h-11 bg-transparent border border-white/15 text-zinc-300 hover:bg-white/[0.05] rounded-sm uppercase tracking-[0.12em] text-xs"
            data-testid="override-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({ pin, reason: reason.trim() })}
            disabled={!canSubmit}
            className="h-11 bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-bold uppercase tracking-[0.12em] text-xs disabled:opacity-40"
            data-testid="override-submit"
          >
            {saving ? 'Authorising…' : 'Authorise edit'}
          </Button>
        </div>
      </div>
    </>
  );
}
